import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";

import Dropdown, { AsyncDropdown } from "components/forms/Dropdown";

import { number as numberParser } from "tools/forms/parsers";
import { number as numberFormatter } from "tools/forms/formatters";

const DropdownField = ({
  control,
  name,

  label,
  placeholder,
  className,
  disabled,

  options = [],
  isAsync = false,
  getOptions,
  defaultOptions,

  required,
  isClearable = false,
  isSearchable = true,

  getFullValue = false,

  type = "text",
  parse,
  format,

  onChange,
  error: forcedError,
  ...rest
}) => {
  const Component = isAsync ? AsyncDropdown : Dropdown;

  const parseFn = useMemo(() => {
    if (parse) return parse;
    if (type === "number") return numberParser;
    return (v) => v;
  }, [parse, type]);

  const formatFn = useMemo(() => {
    if (format) return format;
    if (type === "number") return numberFormatter;
    return (v) => v;
  }, [format, type]);

  const parsedLabel = useMemo(() => {
    if (!label) return undefined;
    return required ? `${label} *` : label;
  }, [label, required]);

  // Convierte el valor guardado en RHF a "option object" para el Dropdown
  const toOptionValue = useCallback(
    (fieldValue) => {
      if (fieldValue == null || fieldValue === "") return null;

      // Si guardas el objeto completo, úsalo tal cual
      if (getFullValue && typeof fieldValue === "object") return fieldValue;

      // Si guardas solo el id/value, busca el option correspondiente
      return options.find((o) => o.value === fieldValue) || null;
    },
    [options, getFullValue]
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...(required ? { required: "Required" } : {}),
      }}
      render={({ field, fieldState }) => {
        const currentOption = toOptionValue(formatFn(field.value));

        const handleSelect = (opt) => {
          const raw = getFullValue ? opt : (opt ? opt.value : null);
          const next = parseFn(raw);

          field.onChange(next);
          if (onChange) onChange(next); // handler custom adicional [web:1][web:2]
        };

        return (
          <Component
            name={field.name}
            value={currentOption}
            onChange={handleSelect}
            onBlur={field.onBlur}
            disabled={disabled}
            className={className}
            label={parsedLabel}
            placeholder={placeholder}
            options={options}
            isClearable={isClearable}
            isSearchable={isSearchable}
            error={forcedError || fieldState.error?.message}
            {...(isAsync ? { getOptions, defaultOptions } : {})}
            {...rest}
          />
        );
      }}
    />
  );
};

DropdownField.propTypes = {
  control: PropTypes.any.isRequired,
  name: PropTypes.string.isRequired,

  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,

  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
    })
  ),
  isAsync: PropTypes.bool,
  getOptions: PropTypes.func,
  defaultOptions: PropTypes.bool,

  required: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,

  getFullValue: PropTypes.bool,

  type: PropTypes.oneOf(["text", "number"]),
  parse: PropTypes.func,
  format: PropTypes.func,

  onChange: PropTypes.func,
  error: PropTypes.string,
};

export default DropdownField;
